
const translations = {
  fr: {
    restart: "Recommencer",
    next: "Suivant",
    finish: "Terminer",
    ofQuestions: ({ current, total }) => `${current} SUR ${total} QUESTIONS`,
    leftTitle: "Ce questionnaire nous aide à personnaliser votre profil santé",
    leftBody:
      "Répondez à quelques questions pour qu'on puisse vous proposer un profil et des recommandations adaptées.",
    leftIncentive:
      "Complétez le quiz et obtenez 10 $ sur votre prochain achat!",
    loading: "Chargement...",
    selectAnswer: "Choisissez une réponse pour continuer.",
    completeTitle: "Merci, votre quiz est complété.",
    completeBody: "Vos résultats ont été envoyés avec succès.",
    resultTitle: "Voici vos pastilles",
    resultCta: "Recevoir ma carte cadeau",
    resultCtaSent: "Carte cadeau envoyée",
    fetchError: "Impossible de charger le quiz",
    submitError: "Impossible d'envoyer la réponse",
    completeError: "Impossible de compléter le quiz",
    emailTitle: "Entrez votre courriel pour commencer le quiz!",
    emailPlaceholder: "votrecourriel@exemple.com",
    emailStart: "Commencer le quiz",
    emailRequired: "Le courriel est requis pour continuer.",
    emailInvalid: "Le format du courriel est invalide.",
    emailUsageNotice:
      "Nous utilisons votre courriel pour sauvegarder vos réponses au quiz et vous envoyer votre carte cadeau de 10 $.",
    emailValidationNotice:
      "Assurez-vous d'entrer une adresse courriel valide. Vous devrez confirmer votre courriel pour recevoir votre carte cadeau.",
    emailSpamNotice:
      "Vérifiez votre dossier de courriels indésirables (spam) si vous ne recevez pas le courriel.",
    emailGiftCardSent: "Carte cadeau envoyée à cette adresse courriel.",
    resultGiftCardSent: ({ email }) => `Carte cadeau envoyée à ${email}.`,
    resultGiftCardSentGeneric: "Carte cadeau envoyée.",
    resultGiftCardSpamNotice:
      "Vérifiez vos courriels indésirables si vous ne la voyez pas.",
    resultTagsInfo:
      "Cliquez sur celles-ci pour accéder à la collection de produits associée.",
  },
  en: {
    restart: "Start over",
    next: "Next",
    finish: "Finish",
    ofQuestions: ({ current, total }) => `${current} OF ${total} QUESTIONS`,
    leftTitle: "This questionnaire helps us personalize your health profile",
    leftBody:
      "Answer a few questions so we can suggest a profile and recommendations tailored to you.",
    leftIncentive: "Complete the quiz and get $10 off your next purchase!",
    loading: "Loading...",
    selectAnswer: "Select an answer to continue.",
    completeTitle: "Thanks, your quiz is complete.",
    completeBody: "Your results were submitted successfully.",
    resultTitle: "Here are your tags",
    resultCta: "Get my gift card",
    resultCtaSent: "Gift card sent",
    fetchError: "Unable to load quiz",
    submitError: "Unable to submit answer",
    completeError: "Unable to complete quiz",
    emailTitle: "Enter your email to start the quiz!",
    emailPlaceholder: "your@email.com",
    emailStart: "Start quiz",
    emailRequired: "Email is required to continue.",
    emailInvalid: "Invalid email format.",
    emailUsageNotice:
      "We use your email to save your quiz answers and send your $10 gift card.",
    emailValidationNotice:
      "Make sure to enter a valid email address. You will need to confirm your email to receive your gift card.",
    emailSpamNotice:
      "Check your spam or junk folder if you don't receive the email.",
    emailGiftCardSent: "Gift card sent to this email address.",
    resultGiftCardSent: ({ email }) => `Gift card sent to ${email}.`,
    resultGiftCardSentGeneric: "Gift card sent.",
    resultGiftCardSpamNotice: "Check your spam folder if you don't see it.",
    resultTagsInfo:
      "Click on them to access the associated product collection.",
  },
};

 function getTranslations(locale) {
  return translations[locale] ?? translations.fr;
}

 function formatQuestionProgress(locale, current, total) {
  const labels = getTranslations(locale);
  return labels.ofQuestions({ current, total });
}

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function buildHttpError(response, method, url, parsedPayload = null) {
  let message = `${method} ${url} failed: ${response.status} ${response.statusText}`;

  const payload =
    parsedPayload === null ? await parseJsonSafely(response) : parsedPayload;

  if (payload) {
    const serverMessage =
      payload?.message ||
      payload?.error ||
      (Array.isArray(payload?.errors)
        ? payload.errors.join(", ")
        : typeof payload?.errors === "object" && payload?.errors
          ? Object.values(payload.errors).flat().join(", ")
          : "");

    if (serverMessage) {
      message = `${message} - ${serverMessage}`;
    }
  }

  return new Error(message);
}

 async function getJson(url, options = {}) {
  const response = await fetch(url, {
    method: "GET",
    ...options,
  });

  const payload = await parseJsonSafely(response.clone());

  if (!response.ok) {
    throw await buildHttpError(response, "GET", url, payload);
  }

  return payload;
}

 async function postJson(url, payload, options = {}) {
  const { headers: optionHeaders = {}, ...restOptions } = options;
  const requestHeaders = {
    "Content-Type": "application/json",
    ...optionHeaders,
  };

  const response = await fetch(url, {
    method: "POST",
    ...restOptions,
    headers: requestHeaders,
    body: JSON.stringify(payload),
  });

  const responsePayload = await parseJsonSafely(response.clone());

  if (!response.ok) {
    throw await buildHttpError(response, "POST", url, responsePayload);
  }

  return responsePayload;
}


const API_BASE_URLS = {
  prod: "https://api-fute-main-qy3nex.laravel.cloud",
  test: "https://api-fute-main-qy3nex.laravel.cloud",
};

function normalizeBaseUrl(value) {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

function buildHeaders(shopifyUserId) {
  if (!shopifyUserId) {
    return {};
  }

  return {
    "X-Shopify-User-Id": String(shopifyUserId),
  };
}

function getData(response) {
  return response?.data ?? response;
}

function isLocalHost(hostname) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0"
  );
}

function resolveDefaultBaseUrl() {
  const envKey = window.PROFIL_FUTE_API_ENV;

  if (envKey === "test") {
    return API_BASE_URLS.test;
  }

  if (envKey === "prod") {
    return API_BASE_URLS.prod;
  }

  if (isLocalHost(window.location.hostname)) {
    return API_BASE_URLS.test;
  }

  return API_BASE_URLS.prod;
}

 function createProfilFuteApi({ baseUrl, shopifyUserId } = {}) {
  const resolvedBaseUrl = normalizeBaseUrl(
    baseUrl ?? window.PROFIL_FUTE_API_BASE_URL ?? resolveDefaultBaseUrl(),
  );
  const headers = buildHeaders(shopifyUserId);

  return {
    async getQuestions() {
      return getJson(`${resolvedBaseUrl}/api/profil-fute/questions`, {
        headers,
      });
    },

    async startSession() {
      const response = await postJson(
        `${resolvedBaseUrl}/api/profil-fute/sessions`,
        {},
        { headers },
      );
      return getData(response);
    },

    async getSession(sessionId) {
      return getJson(
        `${resolvedBaseUrl}/api/profil-fute/sessions/${sessionId}`,
        {
          headers,
        },
      );
    },

    async getSessionProfiles(sessionId) {
      return getJson(
        `${resolvedBaseUrl}/api/profil-fute/sessions/${sessionId}/profiles`,
        {
          headers,
        },
      );
    },

    async getProfiles() {
      return getJson(`${resolvedBaseUrl}/api/profil-fute/profiles`, {
        headers,
      });
    },

    async submitAnswer(sessionId, payload) {
      return postJson(
        `${resolvedBaseUrl}/api/profil-fute/sessions/${sessionId}/answers`,
        payload,
        { headers },
      );
    },

    async completeSession(sessionId, payload = {}) {
      return postJson(
        `${resolvedBaseUrl}/api/profil-fute/sessions/${sessionId}/complete`,
        payload,
        { headers },
      );
    },
  };
}


const locale =
  window.PROFIL_FUTE_LOCALE ??
  (window.location.pathname.includes("/en/") ? "en" : "fr");
  

const circleTagUrls = Array.from(
  { length: 6 },
  (_, index) =>
    `https://api-fute-main-qy3nex.laravel.cloud/api/profil-fute/images/${index + 1}.svg${locale === "en" ? "?lang=en" : ""}`,
);

const circleTagsMarkup = circleTagUrls
  .map((url, index) => {
 
    const overlapClass =
      index === 0 ? "tag-circle-first" : "tag-circle-overlap";
    return `<img src="${url}" alt="Tag ${index + 1}" class="tag-circle ${overlapClass}" />`;
  })
  .join("");
const el = document.querySelector("#quizz-futee");
const shopifyUserId =  el?.dataset.customerId ? Number(el.dataset.customerId) : null;

const labels = getTranslations(locale);
const api = createProfilFuteApi({ shopifyUserId });
const PROFILE_IMAGE_BASE_URL =
  "https://api-fute-main-qy3nex.laravel.cloud/api/profil-fute/images";

function getProfileIdsFromQuery() {
  const params = new URLSearchParams(window.location.search);
  const ids = [params.get("p1"), params.get("p2")]
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0);

  return Array.from(new Set(ids));
}

const urlProfileIds = getProfileIdsFromQuery();

const state = {
  sessionId: null,
  totalQuestions: 5,
  questions: [],
  answeredCount: 0,
  currentQuestion: null,
  selectedAnswerId: null,
  isLoading: false,
  completed: false,
  completedOnServer: false,
  topProfiles: [],
  error: "",
  resultEmail: "",
  resultEmailSaved: false,
  urlProfileIds,
  urlProfiles: [],
  urlProfilesLoaded: false,
  urlProfilesLoading: false,
};

el.innerHTML = `
  <main class="app-root">
    <div id="questionnaire-overlay" class="questionnaire-overlay is-hidden" role="dialog" aria-modal="true" aria-labelledby="question-title" aria-hidden="true">
      <div class="questionnaire-modal">
        <button id="close-questionnaire" class="close-modal-btn" type="button" aria-label="Close modal">×</button>

        <div id="modal-content-grid" class="modal-content-grid">
          <aside id="left-panel" class="left-panel">
            <img class="left-panel-background" src="https://cdn.shopify.com/s/files/1/0239/5186/7968/files/background.jpg?v=1772117153" alt="Questionnaire background" />

            <div class="left-panel-content">
              <div class="left-logo-wrap">
                <img src="https://cdn.shopify.com/s/files/1/0239/5186/7968/files/quizz-logo.png?v=1772117165" alt="Shop Santé" class="left-logo" />
              </div>

              <p class="left-title">${labels.leftTitle}</p>
              <p class="left-body">
              ${labels.leftBody}
              </p>
              <p class="left-incentive">${labels.leftIncentive}</p>

              <div class="left-tags" aria-label="Tags">
                ${circleTagsMarkup}
              </div>
            </div>
          </aside>

          <section id="right-panel" class="right-panel right-panel-quiz">
            <div id="question-progress" class="question-progress">${formatQuestionProgress(locale, 1, state.totalQuestions)}</div>
            <button id="restart-quiz" class="restart-btn" type="button">${labels.restart}</button>

            <h3 id="question-title" class="question-title"></h3>

            <div id="answers-container" class="answers-grid"></div>

            <p id="quiz-status" class="quiz-status quiz-status-error"></p>

            <div id="question-footer" class="question-footer">
              <button id="next-question" class="next-btn" type="button">${labels.next}</button>
            </div>
          </section>

          <section id="result-panel" class="result-panel is-hidden">
            <h3 id="result-title" class="result-title"></h3>
            <div id="result-content"></div>
            <p id="result-status" class="quiz-status quiz-status-error"></p>
          </section>
        </div>
      </div>
    </div>
  </main>
`;


const questionProgressElement = document.querySelector("#question-progress");
const questionTitleElement = document.querySelector("#question-title");
const answersContainerElement = document.querySelector("#answers-container");
const nextQuestionButtonElement = document.querySelector("#next-question");
const restartQuizButtonElement = document.querySelector("#restart-quiz");
const quizStatusElement = document.querySelector("#quiz-status");
const leftPanelElement = document.querySelector("#left-panel");
const rightPanelElement = document.querySelector("#right-panel");
const resultPanelElement = document.querySelector("#result-panel");
const resultTitleElement = document.querySelector("#result-title");
const resultContentElement = document.querySelector("#result-content");
const resultStatusElement = document.querySelector("#result-status");
const modalContentGridElement = document.querySelector("#modal-content-grid");
const questionFooterElement = document.querySelector("#question-footer");
const questionnaireOverlayElement = document.querySelector(
  "#questionnaire-overlay",
);
const closeModalButtonElement = document.querySelector("#close-questionnaire");
const openModalButtonElement = document.querySelector("#open-questionnaire");
const externalOpenModalButtonElement = document.querySelector(
  "#open-profil-fute-modal",
);

let hasInitializedQuiz = false;

async function initializeDirectResultView() {
  state.completed = true;
  state.completedOnServer = false;
  renderQuestionState();
  await loadProfilesFromUrlParams();
}

function ensureQuizInitialized() {
  if (hasInitializedQuiz) {
    return;
  }

  hasInitializedQuiz = true;

  if (state.urlProfileIds.length > 0) {
    initializeDirectResultView();
  } else {
    loadProfilesFromUrlParams();
    initializeQuiz();
  }
}

function openModal() {
  if (!questionnaireOverlayElement) {
    return;
  }

  
  ensureQuizInitialized();
  questionnaireOverlayElement.classList.remove("is-hidden");
  questionnaireOverlayElement.setAttribute("aria-hidden", "false");
  document.body.style.overflow = "hidden";
}

function closeModal() {
  if (!questionnaireOverlayElement) {
    return;
  }

  questionnaireOverlayElement.classList.add("is-hidden");
  questionnaireOverlayElement.setAttribute("aria-hidden", "true");
  document.body.style.overflow = "";
}

function getProfileImageUrl(profileId) {
  return `${PROFILE_IMAGE_BASE_URL}/${profileId}.svg`;
}

function extractTopProfiles(source) {
  const singularProfiles = [source?.top_profile, source?.secondary_profile]
    .filter(Boolean)
    .map((profile, index) => ({
      ...profile,
      rank: typeof profile.rank === "number" ? profile.rank : index + 1,
    }));

  const rows = Array.isArray(source)
    ? source
    : singularProfiles.length > 0
      ? singularProfiles
      : Array.isArray(source?.top_profiles)
        ? source.top_profiles
        : Array.isArray(source?.profiles)
          ? source.profiles
          : Array.isArray(source?.results)
            ? source.results
            : [];

  return rows
    .filter((row) => row?.profil_fute_id ?? row?.id)
    .sort((a, b) => {
      const rankA =
        typeof a.rank === "number" ? a.rank : Number.MAX_SAFE_INTEGER;
      const rankB =
        typeof b.rank === "number" ? b.rank : Number.MAX_SAFE_INTEGER;
      return rankA - rankB;
    })
    .slice(0, 2);
}

function getProfileId(profile) {
  return profile?.profil_fute_id ?? profile?.id ?? null;
}

function getProfileWebsiteUrl(profile) {
  return profile?.website_page_url ?? profile?.website_url ?? null;
}

function getData(payload) {
  return payload?.data ?? payload ?? {};
}

function extractQuestions(payload) {
  const data = getData(payload);

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data.questions)) {
    return data.questions;
  }

  if (Array.isArray(payload?.questions)) {
    return payload.questions;
  }

  return [];
}

function extractProfiles(payload) {
  const data = getData(payload);

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data.profiles)) {
    return data.profiles;
  }

  if (Array.isArray(payload?.profiles)) {
    return payload.profiles;
  }

  return [];
}

function getQuestionText(question) {
  if (!question) {
    return "";
  }

  if (locale === "en") {
    return question.question_en ?? question.question_fr ?? "";
  }

  return question.question_fr ?? question.question_en ?? "";
}

function getAnswerText(answer) {
  if (locale === "en") {
    return answer.answer_en ?? answer.answer_fr ?? "";
  }

  return answer.answer_fr ?? answer.answer_en ?? "";
}

function getCurrentQuestionNumber() {
  if (state.completed) {
    return state.totalQuestions;
  }

  if (!state.currentQuestion) {
    return Math.max(1, Math.min(state.answeredCount, state.totalQuestions));
  }

  return Math.min(state.answeredCount + 1, state.totalQuestions);
}

function setStatus(message = "", isError = false) {
  const nextClassName = isError
    ? "quiz-status quiz-status-error"
    : "quiz-status quiz-status-success";

  if (quizStatusElement) {
    quizStatusElement.textContent = message;
    quizStatusElement.className = nextClassName;
  }

  if (resultStatusElement) {
    resultStatusElement.textContent = message;
    resultStatusElement.className = nextClassName;
  }
}

function formatGiftCardSentMessage() {
  if (!state.resultEmail) {
    return labels.resultGiftCardSentGeneric;
  }

  if (typeof labels.resultGiftCardSent === "function") {
    return labels.resultGiftCardSent({ email: state.resultEmail });
  }

  return labels.resultGiftCardSentGeneric;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function updateFooterVisibility() {
  const isResultStep = state.completed;
  const shouldHideFooter = isResultStep;

  if (nextQuestionButtonElement) {
    nextQuestionButtonElement.classList.toggle("is-hidden", shouldHideFooter);
  }

  if (restartQuizButtonElement) {
    restartQuizButtonElement.classList.toggle("is-hidden", isResultStep);
  }

  if (questionProgressElement) {
    questionProgressElement.classList.toggle("is-hidden", isResultStep);
  }

  if (questionFooterElement) {
    questionFooterElement.classList.toggle("is-hidden", shouldHideFooter);
  }
}

function updateResultLayout() {
  if (!leftPanelElement || !rightPanelElement || !resultPanelElement) {
    return;
  }

  if (state.completed) {
    leftPanelElement.classList.add("is-hidden");
    rightPanelElement.classList.add("is-hidden");
    resultPanelElement.classList.remove("is-hidden");
    modalContentGridElement?.classList.add("modal-content-grid-result");
    return;
  }

  leftPanelElement.classList.remove("is-hidden");
  rightPanelElement.classList.remove("is-hidden");
  resultPanelElement.classList.add("is-hidden");
  modalContentGridElement?.classList.remove("modal-content-grid-result");
  rightPanelElement.className = "right-panel right-panel-quiz";
}

function renderAnswers(question) {
  if (!answersContainerElement) {
    return;
  }

  if (!question) {
    answersContainerElement.classList.add("answers-grid-loading");
    answersContainerElement.innerHTML = `
      <div class="answers-placeholder" aria-hidden="true">
        <div class="answer-placeholder-row"></div>
        <div class="answer-placeholder-row"></div>
        <div class="answer-placeholder-row"></div>
        <div class="answer-placeholder-row"></div>
      </div>
    `;
    return;
  }

  answersContainerElement.classList.remove("answers-grid-loading");

  const answers = Array.isArray(question.answers) ? question.answers : [];

  answersContainerElement.innerHTML = answers
    .map((answer) => {
      const isSelected = state.selectedAnswerId === answer.id;
      const selectedClass = isSelected ? "answer-btn-selected" : "";
      const dotClass = isSelected
        ? "answer-dot answer-dot-selected"
        : "answer-dot";
      const spanClass =
        answer === answers[answers.length - 1] && answers.length % 2 === 1
          ? "answer-btn answer-btn-full"
          : "answer-btn";

      return `
        <button
          class="${spanClass} ${selectedClass}"
          type="button"
          data-answer-id="${answer.id}"
        >
          <span class="${dotClass}"></span>
          ${getAnswerText(answer)}
        </button>
      `;
    })
    .join("");
}

function renderResultScreen() {
  if (!resultContentElement || !resultTitleElement) {
    return;
  }

  const resolvedProfiles =
    Array.isArray(state.urlProfiles) && state.urlProfiles.length > 0
      ? state.urlProfiles
      : state.topProfiles;
  const topProfiles = Array.isArray(resolvedProfiles)
    ? resolvedProfiles.slice(0, 2)
    : [];
  const shouldShowUrlProfilesLoading =
    state.urlProfileIds.length > 0 &&
    state.urlProfilesLoading &&
    state.urlProfiles.length === 0;

  const circlesMarkup = topProfiles
    .map((profile) => {
      const profileId = getProfileId(profile);

      if (!profileId) {
        return "";
      }

      const websiteUrl =
        getProfileWebsiteUrl(profile) || "https://shopsante.ca/";
      const imageMarkup = `
        <img
          src="${getProfileImageUrl(profileId)}"
          alt="Profile ${profileId}"
          class="result-circle"
        />
      `;

      return `
        <a class="result-circle-link" href="${websiteUrl}" target="_blank" rel="noopener noreferrer">
          ${imageMarkup}
        </a>
      `;
    })
    .join("");

  const isGiftCardSent = state.completedOnServer;
  const isUrlParamMode = state.urlProfileIds.length > 0 && !state.sessionId;
  const showEmailInput = !shopifyUserId && !isGiftCardSent && !isUrlParamMode;
  const isEmailValid = isValidEmail(state.resultEmail);
  const disableCta = showEmailInput
    ? !isEmailValid || state.isLoading
    : state.isLoading;
  const ctaLabel = labels.resultCta;
  const giftCardMessage = isGiftCardSent ? formatGiftCardSentMessage() : "";

  resultTitleElement.textContent = labels.resultTitle;
  resultContentElement.innerHTML = `
    <div class="result-screen">
      <div class="result-circles-wrap">
        ${
          shouldShowUrlProfilesLoading
            ? `<p class="result-loading">${labels.loading}</p>`
            : circlesMarkup ||
              `<p class="text-[14px] text-[#353535]">${labels.completeBody}</p>`
        }
      </div>

      ${
        showEmailInput
          ? `
      <div class="result-email-card">
        <label for="result-email" class="result-email-label">Email</label>
        <input
          id="result-email"
          type="email"
          value="${state.resultEmail}"
          placeholder="${labels.emailPlaceholder}"
          class="result-email-input"
        />
        <p class="result-email-notice">${labels.emailUsageNotice}</p>
        <p class="result-email-notice result-email-notice-warning">${labels.emailValidationNotice}</p>
        <p class="result-email-notice result-email-notice-spam" id="result-email-spam-notice" style="display:none">${labels.emailSpamNotice}</p>
        <button
          id="receive-gift-card"
          type="button"
          class="result-cta-btn result-cta-btn-full ${disableCta ? "result-cta-btn-disabled" : ""}"
          ${disableCta ? "disabled" : ""}
        >
          ${ctaLabel}
        </button>
      </div>
      `
          : isGiftCardSent && giftCardMessage
            ? `
      <div class="result-email-card result-email-card-info">
        <p class="result-tags-info-message">${labels.resultTagsInfo}</p>
      </div>
      <div class="result-email-card result-email-card-success">
        <p class="result-gift-card-title">${locale === "fr" ? "Bravo!" : "Congrats!"}</p>
        <p class="result-gift-card-message">${giftCardMessage}</p>
        <p class="result-gift-card-spam-notice">${labels.resultGiftCardSpamNotice}</p>
      </div>
      `
            : !isGiftCardSent && !isUrlParamMode
              ? `
      <button
        id="receive-gift-card"
        type="button"
        class="result-cta-btn ${disableCta ? "result-cta-btn-disabled" : ""}"
        ${disableCta ? "disabled" : ""}
      >
        ${ctaLabel}
      </button>
      `
              : ""
      }

      ${
        isUrlParamMode
          ? `
      <div class="result-email-card result-email-card-info">
        <p class="result-tags-info-message">${labels.resultTagsInfo}</p>
      </div>
      `
          : !isGiftCardSent
            ? `
      <button
        id="restart-quiz-inline"
        type="button"
        class="result-restart-btn"
      >
        ${labels.restart}
      </button>
      `
            : ""
      }
    </div>
  `;

  // Trigger tag animations after rendering
  setTimeout(() => {
    const tagLinks = resultContentElement.querySelectorAll(
      ".result-circle-link",
    );
    tagLinks.forEach((link, index) => {
      // Add bounce-in animation with stagger
      setTimeout(() => {
        link.style.animation =
          "tagBounceIn 0.6s ease-out forwards, tagPulse 2s ease-in-out infinite";
      }, index * 150);
    });
  }, 100);
}

function updateNextButton() {
  if (!nextQuestionButtonElement) {
    return;
  }

  const isLastQuestion =
    Boolean(state.currentQuestion) &&
    state.answeredCount + 1 >= state.totalQuestions;

  const shouldDisable =
    state.isLoading ||
    state.completed ||
    !state.currentQuestion ||
    !state.selectedAnswerId;

  nextQuestionButtonElement.textContent = isLastQuestion
    ? labels.finish
    : labels.next;

  nextQuestionButtonElement.disabled = Boolean(shouldDisable);
  nextQuestionButtonElement.className = shouldDisable
    ? "next-btn next-btn-disabled"
    : "next-btn";
}

function renderQuestionState() {
  if (!questionProgressElement || !questionTitleElement) {
    return;
  }

  updateResultLayout();

  if (state.completed) {
    renderResultScreen();
    updateFooterVisibility();
    return;
  }

  questionProgressElement.textContent = formatQuestionProgress(
    locale,
    getCurrentQuestionNumber(),
    state.totalQuestions,
  );

  questionTitleElement.textContent = state.currentQuestion
    ? getQuestionText(state.currentQuestion)
    : "";
  questionTitleElement.classList.toggle(
    "question-title-placeholder",
    !state.currentQuestion,
  );

  renderAnswers(state.currentQuestion);
  updateNextButton();
  updateFooterVisibility();
}

function updateResultCtaState() {
  if (!resultContentElement) {
    return;
  }

  const ctaButton = resultContentElement.querySelector("#receive-gift-card");
  if (!ctaButton) {
    return;
  }

  const isEmailStep = Boolean(
    resultContentElement.querySelector("#result-email"),
  );
  const shouldDisable = isEmailStep
    ? !isValidEmail(state.resultEmail) || state.isLoading
    : state.isLoading;

  ctaButton.disabled = Boolean(shouldDisable);
  ctaButton.classList.toggle("result-cta-btn-disabled", shouldDisable);
}

async function completeQuiz() {
  if (!state.sessionId) {
    return false;
  }

  try {
    if (state.completedOnServer) {
      return true;
    }

    const payload =
      !shopifyUserId && state.resultEmail && isValidEmail(state.resultEmail)
        ? { email: state.resultEmail }
        : {};
    const shouldSubmitEmail = !shopifyUserId && Boolean(payload.email);

    const completionResponse = await api.completeSession(
      state.sessionId,
      payload,
    );
    const completionData = getData(completionResponse);
    const extractedProfiles = extractTopProfiles(completionData);

    if (extractedProfiles.length > 0) {
      state.topProfiles = extractedProfiles;
    }
    state.completedOnServer = true;
    if (shouldSubmitEmail) {
      state.resultEmailSaved = true;
    }
    setStatus("");
    return true;
  } catch (error) {
    setStatus(`${labels.completeError}: ${error.message}`, true);
    return false;
  }
}

async function loadSessionProfiles() {
  if (!state.sessionId) {
    return;
  }

  try {
    const profilesResponse = await api.getSessionProfiles(state.sessionId);

    const profilesData = getData(profilesResponse);

    const extractedProfiles = extractTopProfiles(profilesData);


    if (extractedProfiles.length > 0) {
      state.topProfiles = extractedProfiles;
    }
  } catch {}
}

async function loadProfilesFromUrlParams() {
  if (state.urlProfilesLoaded || state.urlProfileIds.length === 0) {
    return;
  }

  state.urlProfilesLoading = true;
  state.urlProfilesLoaded = true;
  if (state.completed) {
    renderQuestionState();
  }

  try {
    const profilesPayload = await api.getProfiles();
    const profiles = extractProfiles(profilesPayload);
    const profileMap = new Map(
      profiles.map((profile) => [Number(getProfileId(profile)), profile]),
    );
    const resolvedProfiles = state.urlProfileIds
      .map((id) => profileMap.get(id))
      .filter(Boolean);

    if (resolvedProfiles.length > 0) {
      state.urlProfiles = resolvedProfiles;
      if (state.completed) {
        renderQuestionState();
      }
    }
  } catch {}
  state.urlProfilesLoading = false;
  if (state.completed) {
    renderQuestionState();
  }
}

async function syncSession() {
  if (!state.sessionId) {
    return;
  }

  const response = await api.getSession(state.sessionId);
  const sessionData = getData(response);
  const answers = Array.isArray(sessionData.answers) ? sessionData.answers : [];

  state.answeredCount = answers.length;

  state.currentQuestion =
    response?.next_question ?? sessionData.next_question ?? null;
  state.selectedAnswerId = null;

  if (!state.currentQuestion) {
    state.completed = true;
    state.topProfiles = extractTopProfiles(
      sessionData.results ?? response?.results,
    );
    await loadSessionProfiles();
  }
}

async function initializeQuiz() {
  state.isLoading = true;
  state.completed = false;
  state.completedOnServer = false;
  state.questions = [];
  state.topProfiles = [];
  state.error = "";
  state.selectedAnswerId = null;
  state.resultEmail = "";
  state.resultEmailSaved = false;
  setStatus("");
  renderQuestionState();

  try {
    const questionsPayload = await api.getQuestions();
    const questions = extractQuestions(questionsPayload);
    state.questions = questions;

    if (questions.length > 0) {
      state.totalQuestions = questions.length;
    }

    const session = await api.startSession();
    state.sessionId = session?.id;

    await syncSession();
    setStatus("");
  } catch (error) {
    state.error = error.message;
    setStatus(`${labels.fetchError}: ${error.message}`, true);
  } finally {
    state.isLoading = false;
    renderQuestionState();
  }
}

async function handleNextQuestion() {
  if (state.isLoading || !state.sessionId) {
    return;
  }

  if (!state.currentQuestion) {
    return;
  }

  if (!state.selectedAnswerId) {
    setStatus(labels.selectAnswer, true);
    return;
  }

  state.isLoading = true;
  setStatus("");
  updateNextButton();

  try {
    const answerPayload = {
      question_id: state.currentQuestion.id,
      answer_id: state.selectedAnswerId,
    };

    await api.submitAnswer(state.sessionId, answerPayload);

    await syncSession();
    setStatus("");
  } catch (error) {
    setStatus(`${labels.submitError}: ${error.message}`, true);
  } finally {
    state.isLoading = false;
    renderQuestionState();
  }
}

async function handleReceiveGiftCard() {
  if (state.isLoading) {
    return;
  }

  if (!shopifyUserId && state.resultEmailSaved) {
    return;
  }

  if (!shopifyUserId) {
    const email = state.resultEmail.trim();

    if (!email) {
      setStatus(labels.emailRequired, true);
      renderQuestionState();
      return;
    }

    if (!isValidEmail(email)) {
      setStatus(labels.emailInvalid, true);
      renderQuestionState();
      return;
    }

    state.resultEmail = email;
  }

  const spamNotice = resultContentElement?.querySelector(
    "#result-email-spam-notice",
  );
  if (spamNotice) spamNotice.style.display = "";

  state.isLoading = true;
  setStatus("");
  renderQuestionState();

  try {
    const completionSucceeded = await completeQuiz();

    if (!shopifyUserId && completionSucceeded) {
      state.resultEmailSaved = true;
      setStatus("");
    }
  } finally {
    state.isLoading = false;
    renderQuestionState();
  }
}

function handleContainerClick(event) {
  const restartInlineButton = event.target.closest("#restart-quiz-inline");
  const receiveGiftCardButton = event.target.closest("#receive-gift-card");
  const resultEmailInput = event.target.closest("#result-email");

  if (restartInlineButton) {
    restartQuizButtonElement?.click();
    return;
  }

  if (receiveGiftCardButton) {
    handleReceiveGiftCard();
    return;
  }

  if (resultEmailInput) {
    return;
  }

  const target = event.target.closest("[data-answer-id]");

  if (!target || state.isLoading || state.completed) {
    return;
  }

  state.selectedAnswerId = Number(target.getAttribute("data-answer-id"));
  setStatus("");
  renderQuestionState();
}

answersContainerElement?.addEventListener("click", handleContainerClick);
resultContentElement?.addEventListener("click", handleContainerClick);

function handleContainerInput(event) {
  const target = event.target;

  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  if (target.id !== "result-email") {
    return;
  }

  if (state.resultEmailSaved) {
    return;
  }

  state.resultEmail = target.value;
  updateResultCtaState();
}

answersContainerElement?.addEventListener("input", handleContainerInput);
resultContentElement?.addEventListener("input", handleContainerInput);

nextQuestionButtonElement?.addEventListener("click", async () => {
  await handleNextQuestion();
});

restartQuizButtonElement?.addEventListener("click", async () => {
  await initializeQuiz();
});

openModalButtonElement?.addEventListener("click", openModal);
externalOpenModalButtonElement?.addEventListener("click", openModal);

closeModalButtonElement?.addEventListener("click", closeModal);

if (state.urlProfileIds.length > 0) {
  openModal();
}

// Auto-open modal when coming from landing page
if (new URLSearchParams(window.location.search).get("open") === "1") {
  openModal();
}
